import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Grid,
  Pagination,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Menu,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
  AssignmentReturn as ReturnIcon,
  HelpOutline as HelpOutlineIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import purchaseOrderService from '../../services/purchaseOrderService';
import Layout from '../../components/layout/Layout';

const ViewPurchaseReturnsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [returnedOrders, setReturnedOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    itemsPerPage: 10
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedReturnId, setSelectedReturnId] = useState(null);
  const [returnDetailOpen, setReturnDetailOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  
  // Menu state
  const isMenuOpen = Boolean(menuAnchorEl);

  const loadReturnData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await purchaseOrderService.getReturnedPurchaseOrders();
        
        // Filter orders to make sure they are RETURNED status
        const filteredOrders = Array.isArray(data) ? 
          data.filter(order => order && order.status === 'RETURNED') : 
          [];
          
        if (filteredOrders.length === 0 && Array.isArray(data) && data.length > 0) {
          console.warn('No purchase orders with RETURNED status found in data');
        }
        
      // Sort by orderDate (descending)
      filteredOrders.sort((a, b) => {
        const dateA = a.orderDate ? new Date(a.orderDate) : new Date(0);
        const dateB = b.orderDate ? new Date(b.orderDate) : new Date(0);
        return dateB - dateA;
      });
      
      // For each return, enrich with supplier data if needed
      const enrichedReturns = await Promise.all(filteredOrders.map(async (order) => {
        // Create a new order object with properly mapped fields
        const enhancedOrder = {
          ...order,
          // Use orderNumber if available, otherwise fallback to id
          displayId: order.orderNumber || `#${order.id}`,
        };
        
        // Format date properly
        if (order.orderDate) {
          try {
            const parsedDate = new Date(order.orderDate);
            if (!isNaN(parsedDate.getTime())) {
              enhancedOrder.orderDate = `${parsedDate.getMonth() + 1}/${parsedDate.getDate()}/${parsedDate.getFullYear()}`;
            }
          } catch (error) {
            console.error('Error parsing date:', error);
          }
        }
        
        // Handle supplier data with higher priority on supplier object
        if (order.supplier) {
          if (typeof order.supplier === 'object') {
            enhancedOrder.supplierName = order.supplier.name || 'Unknown Supplier';
          } else if (typeof order.supplier === 'string') {
            enhancedOrder.supplierName = order.supplier;
          }
        } else if (order.supplierId) {
          try {
            const supplierData = await purchaseOrderService.getSupplierById(order.supplierId);
            if (supplierData) {
              enhancedOrder.supplierName = supplierData.name || 'Unknown Supplier';
            } else {
              enhancedOrder.supplierName = 'Unknown Supplier';
            }
          } catch (error) {
            console.error(`Error fetching supplier for order ${order.id}:`, error);
            enhancedOrder.supplierName = 'Unknown Supplier';
          }
        } else {
          enhancedOrder.supplierName = 'Unknown Supplier';
        }
        
        return enhancedOrder;
      }));
      
      setReturnedOrders(enrichedReturns);
      setFilteredOrders(enrichedReturns);
      setPagination({
        ...pagination,
        totalElements: enrichedReturns.length,
        totalPages: Math.ceil(enrichedReturns.length / pagination.itemsPerPage)
      });
      } catch (error) {
        console.error('Failed to load purchase return data:', error);
        setError('Failed to load return data. Please try again later.');
      } finally {
        setLoading(false);
      }
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    loadReturnData();
  }, [loadReturnData]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A'; // Check if date is valid
      return date.toLocaleDateString();
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'N/A';
    }
  };

  // Extract return information from order notes if not available directly
  const extractReturnInfoFromNotes = (notes) => {
    if (!notes || typeof notes !== 'string') return 'Not specified';
    
    // Check if the notes contain a reason
    if (notes.includes('Reason:')) {
      const reasonPart = notes.split('Reason:')[1];
      if (reasonPart) {
        // If there's more content after "Reason:", split on periods or new lines to get just the reason
        const endMatch = reasonPart.match(/[.;\n\r]/);
        if (endMatch && endMatch.index > 0) {
          return reasonPart.substring(0, endMatch.index).trim();
        }
        return reasonPart.trim();
      }
    }
    
    return notes;
  };

  // Get supplier name from order
  const getSupplierName = (order) => {
    if (!order) return 'N/A';
    
    if (order.supplierName) {
      return order.supplierName;
    }
    
    if (order.supplier) {
      if (typeof order.supplier === 'object') {
        return order.supplier.name || 'Unknown Supplier';
      }
      return order.supplier;
    }
    
    return 'Unknown Supplier';
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setFilteredOrders(returnedOrders);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = returnedOrders.filter(order => 
      (order.orderNumber && order.orderNumber.toLowerCase().includes(query)) ||
      (order.originalOrderNumber && order.originalOrderNumber.toLowerCase().includes(query)) ||
      (order.returnReason && order.returnReason.toLowerCase().includes(query)) ||
      (order.id && order.id.toString().includes(query)) ||
      (getSupplierName(order).toLowerCase().includes(query)) ||
      (order.notes && order.notes.toLowerCase().includes(query))
    );
    
    setFilteredOrders(filtered);
    setPagination({
      ...pagination,
      page: 1,
      totalPages: Math.ceil(filtered.length / pagination.itemsPerPage)
    });
  };

  // Filter orders based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOrders(returnedOrders);
      setPagination({
        ...pagination,
        page: 1,
        totalPages: Math.ceil(returnedOrders.length / pagination.itemsPerPage)
      });
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = returnedOrders.filter(order => 
      (order.orderNumber && order.orderNumber.toLowerCase().includes(query)) ||
      (order.originalOrderNumber && order.originalOrderNumber.toLowerCase().includes(query)) ||
      (order.returnReason && order.returnReason.toLowerCase().includes(query)) ||
      (order.id && order.id.toString().includes(query)) ||
      (getSupplierName(order).toLowerCase().includes(query)) ||
      (order.notes && order.notes.toLowerCase().includes(query))
    );
    
    setFilteredOrders(filtered);
    setPagination({
      ...pagination,
      page: 1,
      totalPages: Math.ceil(filtered.length / pagination.itemsPerPage)
    });
  }, [searchQuery, returnedOrders, pagination.itemsPerPage]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      page: value
    });
  };

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (pagination.page - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  };

  // Handle menu open
  const handleMenuOpen = (event, returnId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedReturnId(returnId);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedReturnId(null);
  };

  // Status chip color mapping
  const getStatusColor = (status) => {
    switch(status) {
      case 'RETURNED': return 'error';
      case 'PENDING': return 'warning';
      case 'PROCESSING': return 'info';
      case 'SHIPPED': return 'primary';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  // Handle view return details
  const handleViewReturn = async (returnOrder) => {
    setSelectedReturn(returnOrder);
    setReturnDetailOpen(true);
  };

  // Download invoice for a return order
  const handleDownloadInvoice = async (orderId) => {
    try {
      setDownloadLoading(true);
      
      // First, get the return details to find the original order (same as sales returns)
      const returnDetails = await purchaseOrderService.getPurchaseOrderById(orderId);
      
      // Generate receipt
      const receiptBlob = await purchaseOrderService.generateReturnReceipt(orderId);
      
      // Create URL for the blob
      const url = window.URL.createObjectURL(receiptBlob);
      
      // Prepare filename for download
      const fileName = `Purchase-Return-Receipt-${orderId}.pdf`;
      
      // Create a hidden anchor element for downloading
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = fileName;
      downloadLink.target = '_blank';
      
      // Add to DOM, click, then remove
      document.body.appendChild(downloadLink);
      
      // First open for viewing in new tab
      window.open(url, '_blank');
      
      // Then trigger download
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Show success message
      enqueueSnackbar('Return receipt downloaded successfully', { variant: 'success' });
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error(`Error generating return receipt for order ${orderId}:`, error);
      enqueueSnackbar('Failed to generate return receipt', { variant: 'error' });
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Layout>
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
            <ReturnIcon sx={{ mr: 1 }} />
          Purchase Return Orders
        </Typography>
        </Box>
        
        {/* Search and filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search by order number, supplier, or notes"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" sx={{ ml: 2 }}>
              Search
            </Button>
            <IconButton sx={{ ml: 1 }}>
              <FilterIcon />
            </IconButton>
          </Box>
        </Paper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Loading indicator */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {/* Table View instead of Accordions */}
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Return #</TableCell>
                <TableCell>Original Order</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getCurrentPageItems().length > 0 ? (
                getCurrentPageItems().map((order) => (
                  <TableRow key={order.id || `order-${Math.random()}`} hover>
                    <TableCell>
                      {order.orderNumber || 'No Order Number'}
                    </TableCell>
                    <TableCell>
                      {order.originalOrderNumber || order.orderNumber?.replace('RETURN-PO-', 'PO-') || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {order.returnReason || extractReturnInfoFromNotes(order.notes) || '-'}
                    </TableCell>
                    <TableCell>
                      {formatDate(order.orderDate)}
                    </TableCell>
                    <TableCell>{getSupplierName(order)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={order.status || 'UNKNOWN'} 
                        color="error" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      ₹{(Number(order.total) || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewReturn(order)}
                          title="View Return Details"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleDownloadInvoice(order.id)}
                          title="Print Return Receipt"
                          disabled={downloadLoading}
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, order.id)}
                          title="More Actions"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
          </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {loading ? 'Loading returns...' : 'No returns found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Pagination */}
        {filteredOrders.length > 0 && (
          <>
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination 
                count={pagination.totalPages} 
                page={pagination.page} 
                onChange={handlePageChange} 
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
            
            <Box display="flex" justifyContent="center" mt={1}>
              <Typography variant="body2" color="text.secondary">
                Page {pagination.page} of {pagination.totalPages} ({filteredOrders.length} total returns)
                  </Typography>
            </Box>
          </>
        )}
        
        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            if (selectedReturnId) {
              handleDownloadInvoice(selectedReturnId);
            }
            handleMenuClose();
          }}
          disabled={downloadLoading}
          >
            <PrintIcon fontSize="small" sx={{ mr: 1 }} />
            Print Return Receipt
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedReturnId) {
              handleViewReturn(returnedOrders.find(r => r.id === selectedReturnId));
            }
            handleMenuClose();
          }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            View Return Details
          </MenuItem>
        </Menu>
        
        {/* Return Details Dialog */}
        <Dialog
          open={returnDetailOpen}
          onClose={() => setReturnDetailOpen(false)}
          aria-labelledby="return-details-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="return-details-dialog-title">
            Return Details
          </DialogTitle>
          <DialogContent dividers>
            {selectedReturn ? (
                  <Box>
                {/* Return header info */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Return Number</Typography>
                    <Typography variant="body1">{selectedReturn.orderNumber || `#${selectedReturn.id}`}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Original Order</Typography>
                    <Typography variant="body1">{selectedReturn.originalOrderNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Return Date</Typography>
                    <Typography variant="body1">{selectedReturn.orderDate || formatDate(selectedReturn.orderDate) || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Supplier</Typography>
                    <Typography variant="body1">{getSupplierName(selectedReturn)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    <Chip 
                      label={selectedReturn.status || 'N/A'} 
                      size="small" 
                      color={getStatusColor(selectedReturn.status)} 
                    />
                  </Grid>
                  {(selectedReturn.returnReason || selectedReturn.notes) && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Return Reason</Typography>
                      <Typography variant="body1">
                        {selectedReturn.returnReason || extractReturnInfoFromNotes(selectedReturn.notes)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Return items */}
                <Typography variant="h6" gutterBottom>Returned Items</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>
                          Return Reason
                          <Tooltip title="The reason provided for returning this item">
                            <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedReturn.items && selectedReturn.items.length > 0 ? (
                        selectedReturn.items.map((item) => {
                          const reason = item.returnReason || extractReturnInfoFromNotes(selectedReturn.notes) || 'Not specified';
                          return (
                            <TableRow key={item.id}>
                              <TableCell>
                                {(item.product && item.product.name) || 
                                 item.productName || 
                                 `Product #${item.productId}`}
                              </TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">
                                {item.unitPrice 
                                  ? new Intl.NumberFormat('en-IN', {
                                      style: 'currency',
                                      currency: 'INR'
                                    }).format(item.unitPrice)
                                  : 'N/A'
                                }
                              </TableCell>
                              <TableCell align="right">
                                {(item.total || (item.unitPrice && item.quantity))
                                  ? new Intl.NumberFormat('en-IN', {
                                      style: 'currency',
                                      currency: 'INR'
                                    }).format(item.total || (item.unitPrice * item.quantity))
                                  : 'N/A'
                                }
                              </TableCell>
                              <TableCell>{reason}</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No items found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6} md={8}></Grid>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">
                          {selectedReturn.subtotal 
                            ? new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR'
                              }).format(selectedReturn.subtotal)
                            : 'N/A'
                          }
                    </Typography>
                      </Box>
                      {selectedReturn.discount && selectedReturn.discount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Discount:</Typography>
                          <Typography variant="body2">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(selectedReturn.discount)}
                      </Typography>
                        </Box>
                    )}
                      {selectedReturn.taxAmount && selectedReturn.taxAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Tax:</Typography>
                    <Typography variant="body2">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(selectedReturn.taxAmount)}
                    </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {selectedReturn.total 
                            ? new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR'
                              }).format(selectedReturn.total)
                            : 'N/A'
                          }
                    </Typography>
                  </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Typography>Loading return details...</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReturnDetailOpen(false)}>Close</Button>
            {selectedReturn && (
              <Button 
                variant="contained" 
                startIcon={<PrintIcon />}
                onClick={() => {
                  handleDownloadInvoice(selectedReturn.id);
                }}
                disabled={downloadLoading}
              >
                Print Receipt
              </Button>
            )}
          </DialogActions>
        </Dialog>
    </Box>
    </Layout>
  );
};

export default ViewPurchaseReturnsPage; 